import {
  QueryFactory,
  InputTemplateType,
  InputTemplateDto,
  CreateInputTemplateDto,
  PatchInputTemplateDto,
} from '@lmt/shared-types';
import {
  DataGrid,
  DXG,
  RowInsertingEvent,
  RowRemovingEvent,
  RowUpdatingEvent,
  useDataGrid,
} from '@lmt/unicorn-shared/app/common/devexpress/Grid/dx-grid';
import { parseError } from '@lmt/unicorn-shared/app/helpers/error-handling';
import { useScopedTranslation } from '@lmt/unicorn-shared/app/helpers/localization/useScopedTranslation';
import { nameofFactory } from '@lmt/unicorn-shared/app/helpers/nameof';
import { useUniDispatch } from '@lmt/unicorn-shared/app/store/types';
import { useModal } from '@lmt/unicorn-shared/app/components/common/ui-kit/modal/useModal';
import { useQueryClient } from '@tanstack/react-query';
import React, { FC, useRef } from 'react';

import { Loading } from '../../../common/Loading';
import { useEventCallback } from '@mui/material';
import {
  allowOnlySingleNewRow,
  useCachedDataSourceWhenEditing,
} from '@lmt/unicorn-shared/app/common/devexpress/Grid/grid-helper';

type Props = {
  subtenantId?: number;
  templateType: InputTemplateType;
  isNumber: boolean;
  fieldId: number | null;
};

const nameof = nameofFactory<InputTemplateDto>();

export const InputTemplatesTable: FC<Props> = (props) => {
  if (!props.subtenantId) {
    return null;
  }
  const dispatch = useUniDispatch();
  const modals = useModal();
  const i18n = useScopedTranslation('input_templates.form');
  const queryClient = useQueryClient();
  useDataGrid();

  const inputTemplatesQuery = QueryFactory.InputTemplateQuery.useGetQuery(
    {
      subtenantId: props.subtenantId,
      type: props.templateType,
      dataBlockFieldId: props.fieldId,
    },
    {
      enabled: !!props.subtenantId,
    },
  );
  const dataGridRef = useRef<DataGrid<InputTemplateDto, string>>(null);
  const dataSource = useCachedDataSourceWhenEditing(
    dataGridRef,
    inputTemplatesQuery.data,
  );

  const updateInputTemplates = useEventCallback(async () => {
    await queryClient.invalidateQueries({
      queryKey: QueryFactory.InputTemplateQuery.getQueryKey(
        props.templateType,
        props.subtenantId,
      ),
    });
  });

  const onRowCreate = useEventCallback(
    (row: RowInsertingEvent<InputTemplateDto>) => {
      async function onRowCreate(data: InputTemplateDto) {
        const dto: CreateInputTemplateDto = {
          ...data,
          name: data.name.trim(),
          value: data.value.toString(),
          subtenantId: props.subtenantId!,
          dataBlockFieldId: props.fieldId,
          type: props.templateType,
        };
        try {
          await QueryFactory.InputTemplateClient.create(dto);
          await updateInputTemplates();
        } catch (error) {
          await modals.showError({ text: parseError(error) });
          return true;
        }
        return false;
      }

      row.cancel = onRowCreate(row.data);
    },
  );

  const onRowEdit = useEventCallback(
    (row: RowUpdatingEvent<InputTemplateDto>) => {
      async function onRowEdit(
        oldData: InputTemplateDto,
        data: Partial<InputTemplateDto>,
      ) {
        const inputTemplate = {
          ...oldData,
          ...data,
        };
        const dto: PatchInputTemplateDto = {
          ...inputTemplate,
          name: inputTemplate.name.trim(),
          value: inputTemplate.value.toString(),
        };
        try {
          await QueryFactory.InputTemplateClient.patch(inputTemplate.id, dto);
        } catch (error) {
          await modals.showError({ text: parseError(error) });
          return true;
        }
        await updateInputTemplates();
        return false;
      }

      row.cancel = onRowEdit(row.oldData, row.newData);
    },
  );

  const onRowDelete = useEventCallback(
    (row: RowRemovingEvent<InputTemplateDto>) => {
      async function onRowDelete(row: InputTemplateDto) {
        const id = row.id;
        try {
          await QueryFactory.InputTemplateClient.remove(id);
          await updateInputTemplates();
        } catch (error) {
          await modals.showError({ text: parseError(error) });
          return true;
        }

        return false;
      }
      row.cancel = onRowDelete(row.data);
    },
  );
  return (
    <Loading loading={inputTemplatesQuery.isLoading} title={i18n.t('loading')}>
      <DXG.DataGrid
        dataSource={dataSource}
        keyExpr={'id'}
        showBorders={true}
        style={{ width: '100%', maxHeight: '500px' }}
        onInitNewRow={(e) => {
          const data = e.data;
          data.id = 10000000 + Math.round(Math.random() * 10000);
        }}
        onRowInserting={onRowCreate}
        onRowUpdating={onRowEdit}
        onRowRemoving={onRowDelete}
        onCellPrepared={(e) => {
          e.cellElement.style.textAlign = 'left';
        }}
        onToolbarPreparing={allowOnlySingleNewRow}
      >
        <DXG.Paging enabled={false} />
        <DXG.Editing
          mode="batch"
          allowUpdating={true}
          allowAdding={true}
          allowDeleting={true}
          selectTextOnEditStart={true}
          startEditAction="click"
          newRowPosition="pageTop"
        />
        <DXG.Column dataField="id" visible={false} />
        <DXG.Column
          sortOrder="asc"
          width={'30%'}
          alignment="left"
          dataField={nameof('name')}
          caption={i18n.t('columns.name')}
          dataType="string"
          sortIndex={0}
        >
          <DXG.RequiredRule />
        </DXG.Column>
        <DXG.Column
          dataField={nameof('value')}
          caption={i18n.t('columns.value')}
          dataType={props.isNumber ? 'number' : 'string'}
        >
          <DXG.RequiredRule />
        </DXG.Column>
        <DXG.Column type="buttons">
          <DXG.Button name="delete" />
        </DXG.Column>
      </DXG.DataGrid>
    </Loading>
  );
};
